import React, { useState } from "react";
import ProfessionalAffiliation from "../../../../../types/ProfessionalAffiliation";
import {
  FormControl,
  FormLabel,
  Box,
  Stack,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

interface ResponsibilitiesInputProps {
  index: number;
  affiliation: ProfessionalAffiliation;
  handleInput: (e: any, index: number, projectIndex?: number) => void;
  projectIndex: number;
  errors: Record<string, string>;
  removeErrorMessage: (field: string) => void;
}

export const ResponsibilitiesInput: React.FC<ResponsibilitiesInputProps> = ({
  affiliation,
  handleInput,
  index,
  projectIndex,
  errors,
  removeErrorMessage,
}) => {
  const [hoveredField, setHoveredField] = useState<number | null>(null);
  const responsibilities = affiliation.projects[projectIndex]
    .responsibilities || [""];

  const handleResponsibilityChange = (idx: number, value: string) => {
    const updatedResponsibilities = [...responsibilities];
    updatedResponsibilities[idx] = value;

    handleInput(
      {
        target: {
          name: "responsibilities",
          value: updatedResponsibilities,
        },
      },
      index,
      projectIndex
    );
  };

  const addResponsibility = () => {
    handleInput(
      {
        target: {
          name: "responsibilities",
          value: [...responsibilities, ""],
        },
      },
      index,
      projectIndex
    );
  };

  const removeResponsibility = (idx: number) => {
    if (responsibilities.length === 1) return;

    const updatedResponsibilities = responsibilities.filter(
      (_, i) => i !== idx
    );

    handleInput(
      {
        target: {
          name: "responsibilities",
          value: updatedResponsibilities,
        },
      },
      index,
      projectIndex
    );
  };

  return (
    <FormControl fullWidth>
      <FormLabel htmlFor={`responsibilities-${index}-${projectIndex}`}>
        Responsibilities
      </FormLabel>
      <Box>
        <Stack spacing={2} mb={2}>
          {responsibilities.map((field, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onMouseEnter={() => setHoveredField(idx)}
              onMouseLeave={() => setHoveredField(null)}
            >
              <TextField
                fullWidth
                multiline
                variant="outlined"
                value={field}
                error={
                  idx === 0
                    ? !!errors[
                        `affiliations[${index}].projects[${projectIndex}].responsibilities`
                      ]
                    : false
                }
                helperText={
                  idx === 0
                    ? errors[
                        `affiliations[${index}].projects[${projectIndex}].responsibilities`
                      ]
                    : null
                }
                placeholder="Responsibilities"
                onChange={(e) => {
                  handleResponsibilityChange(idx, e.target.value);
                  removeErrorMessage(
                    `affiliations[${index}].projects[${projectIndex}].responsibilities`
                  );
                }}
              />
              {idx > 0 &&
                hoveredField === idx && ( // No delete button for first responsibility
                  <IconButton
                    onClick={() => removeResponsibility(idx)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
            </Box>
          ))}
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addResponsibility}
          fullWidth
        >
          Add Another Responsibility
        </Button>
      </Box>
    </FormControl>
  );
};
